import { Fragment, useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DISCREPANCY_SCOPE_LABELS } from '@kn/shared';
import { api } from '../../api/client';
import { MouvementsDuJour } from './MouvementsDuJour';
import { messageErreur } from '../../api/error-message';
import { LegendeVert } from '../../components/LegendeVert';
import { classeMontant } from '../../components/montant';
import { LegendeEcart } from '../../components/LegendeEcart';

/** Au-delà, l'écran devient illisible et lent : le fichier Excel prend le relais. */
const LIGNES_AFFICHEES = 500;

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function formaterDate(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return jour && mois && annee ? `${jour}/${mois}/${annee}` : iso;
}


/**
 * Drill-down d'un écart : jour par jour, code par code, les deux côtés.
 *
 * À la maille famille, c'est le détail **par code** qui répond à la seule
 * question utile : lequel des jumeaux dérive ? Deux cumuls cohabitent donc,
 * celui du code et celui de la clé entière.
 */
export function DrilldownEcart({
  id,
  onFerme,
  jourOuvert = null,
}: {
  id: string;
  /** Absent quand le drill-down occupe une page : il n'y a rien a refermer. */
  onFerme?: () => void;
  /** Journée à ouvrir d'emblée, demandée depuis le fil de vie. */
  jourOuvert?: string | null;
}): ReactElement {
  const [codeFiltre, setCodeFiltre] = useState('');
  /**
   * Journees dépliées. Le detail se charge a l'ouverture, jamais avant.
   *
   * `jourOuvert` sert d'etat initial plutot que d'effet : le parent remonte ce
   * composant (prop `key`) quand le fil de vie demande une journee. Arriver ici
   * sur un tableau replie obligerait a rechercher a la main la journee sur
   * laquelle on vient de cliquer — et un effet qui force l'ouverture
   * empecherait ensuite de la replier.
   */
  const [depliees, setDepliees] = useState<string[]>(jourOuvert ? [jourOuvert] : []);
  const [toutAfficher, setToutAfficher] = useState(jourOuvert !== null);
  /**
   * N13 : ne regarder que ce qui reste. « Rouges seules » pour l'instruction du
   * matin, « rouges + oranges » quand on veut aussi relire ce qui a été
   * expliqué à la main.
   */
  const [filtreEtat, setFiltreEtat] = useState<'TOUTES' | 'ROUGES' | 'ROUGES_ORANGES'>('TOUTES');

  const drilldown = useQuery({
    queryKey: ['discrepancies', 'drilldown', id],
    queryFn: () => api.discrepancyDrilldown(id),
  });

  const lignes = useMemo(() => {
    const toutes = drilldown.data?.lignes ?? [];
    const parCode = codeFiltre ? toutes.filter((l) => l.codpro === codeFiltre) : toutes;
    // N13 : « on ne se penche que sur le rouge » — et parfois sur l'orange,
    // quand on veut relire ce qui a été expliqué à la main.
    if (filtreEtat === 'ROUGES') return parCode.filter((l) => l.etat === 'ROUGE');
    if (filtreEtat === 'ROUGES_ORANGES') {
      return parCode.filter((l) => l.etat === 'ROUGE' || l.etat === 'ORANGE');
    }
    return parCode;
  }, [drilldown.data, codeFiltre, filtreEtat]);

  const visibles = toutAfficher ? lignes : lignes.slice(0, LIGNES_AFFICHEES);
  const parFamille = drilldown.data?.maille === 'FAMILLE';

  return (
    <div className="carte">
      <div className="barre-actions barre-actions--titre">
        <h2>
          Drill-down {drilldown.data ? `— ${drilldown.data.cle} · ${drilldown.data.libelle}` : ''}
        </h2>
        <div className="barre-actions__boutons">
          <a className="bouton bouton--secondaire" href={api.discrepancyDrilldownExportUrl(id)}>
            Exporter en Excel
          </a>
          {onFerme && (
            <button type="button" className="bouton bouton--lien" onClick={onFerme}>
              Fermer
            </button>
          )}
        </div>
      </div>

      {drilldown.isPending && <p className="chargement">Chargement du drill-down…</p>}
      {drilldown.isError && (
        <p className="alerte alerte--erreur">{messageErreur(drilldown.error)}</p>
      )}

      {drilldown.data && (
        <>
          <p className="sous-titre">
            Maille {DISCREPANCY_SCOPE_LABELS[drilldown.data.maille].toLowerCase()} &mdash;{' '}
            {drilldown.data.codpros.length} code(s) agrégé(s), composés de copacking compris &mdash; apparu
            le{' '}
            {formaterDate(drilldown.data.apparuLe)} &mdash; écart cumulé{' '}
            <strong className={classeMontant(drilldown.data.ecartUvc)}>
              {formaterNombre(drilldown.data.ecartUvc)}
            </strong>{' '}
            UVC, cumulés depuis l&apos;ancrage du 01/01/2026.
          </p>

          {drilldown.data.horsControle && (
            <p className="alerte alerte--info" role="note">
              Clé hors contrôle : au Restant, ses mouvements physiques n&apos;apparaissent qu&apos;avec
              « Articles hors périmètre : Affichés ».
            </p>
          )}

          {drilldown.data.composesDeclares.length > 0 && (
            <p className="alerte alerte--attention" role="note">
              <strong>Anomalie d&apos;instruction</strong> &mdash; un composé de cette clé porte des
              lignes de comptabilité matières, alors que la douane ne connaît que le composant
              (règle 15) :{' '}
              {drilldown.data.composesDeclares
                .map((a) => `${a.compose} (${a.lignes.toLocaleString('fr-FR')} lignes GEJNM)`)
                .join(', ')}
              . À instruire avec KN ; le détail voyage dans l&apos;export.
            </p>
          )}

          {parFamille && drilldown.data.codpros.length > 1 && (
            <div className="barre-actions">
              <label className="champ" htmlFor="drilldown-code">
                <span>Isoler un code</span>
                <select
                  id="drilldown-code"
                  value={codeFiltre}
                  onChange={(event) => setCodeFiltre(event.target.value)}
                >
                  <option value="">Tous les codes de la famille</option>
                  {drilldown.data.codpros.map((codpro) => (
                    <option key={codpro} value={codpro}>
                      {codpro}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {/* Le compteur dit en un coup d'oeil ce qui reste a regarder : on ne
              se penche que sur le rouge (regle 16). */}
          <div className="barre-actions">
            <label className="champ" htmlFor="drilldown-etat">
              <span>Journées affichées</span>
              <select
                id="drilldown-etat"
                value={filtreEtat}
                onChange={(event) =>
                  setFiltreEtat(event.target.value as 'TOUTES' | 'ROUGES' | 'ROUGES_ORANGES')
                }
              >
                <option value="TOUTES">Toutes</option>
                <option value="ROUGES">Rouges uniquement</option>
                <option value="ROUGES_ORANGES">Rouges et oranges</option>
              </select>
            </label>
          </div>

          <p className="sous-titre legende">
            <span className="pastille pastille--vert" aria-hidden="true" /> <strong>Verte</strong> :
            tout est apparié ou validé &middot;{' '}
            <span className="pastille pastille--orange" aria-hidden="true" />{' '}
            <strong>Orange</strong> : expliquée, dont une partie à la main &middot;{' '}
            <span className="pastille pastille--rouge" aria-hidden="true" /> <strong>Rouge</strong> :
            il reste à instruire.
          </p>
          <LegendeVert />
          <p className="sous-titre legende">
            Dans le détail d'une journée : <span className="ref-bl__marque">✓</span>{' '}
            <strong>même code</strong> — la contrepartie porte la même référence sous le même
            article &middot; <span className="marque-famille">✓ famille</span>{' '}
            <strong>via famille</strong> — elle est déclarée sous un autre code de la famille
            validée, nommé en regard &middot;{' '}
            <span className="etiquette etiquette--rapproche">Rapproché</span>{' '}
            <strong>à la main</strong> &middot; sans marque : <strong>à instruire</strong>.
          </p>
          <LegendeEcart variante="complete" />

          <div className="totaux totaux--appariement">
            <div>
              <span>Journées vertes</span>
              <strong className="nombre nombre--succes">
                {drilldown.data.joursVerts.toLocaleString('fr-FR')}
              </strong>
            </div>
            <div>
              <span>Journées oranges</span>
              <strong className="nombre">
                {drilldown.data.joursOranges.toLocaleString('fr-FR')}
              </strong>
            </div>
            <div>
              <span>Journées rouges</span>
              <strong
                className={
                  drilldown.data.joursRouges > 0 ? 'nombre nombre--alerte' : 'nombre'
                }
              >
                {drilldown.data.joursRouges.toLocaleString('fr-FR')}
              </strong>
            </div>
            <div>
              <span>Références appariées</span>
              <strong>{drilldown.data.referencesAppariees.toLocaleString('fr-FR')}</strong>
            </div>
            <div>
              <span>Références d&apos;un seul côté</span>
              <strong>{drilldown.data.referencesOrphelines.toLocaleString('fr-FR')}</strong>
            </div>
            <div>
              <span>Fenêtre de recherche</span>
              <strong>± {drilldown.data.fenetreAppariementJours} j</strong>
            </div>
          </div>

          <p className="sous-titre">
            Une journée est verte quand tous ses mouvements sont appariés par référence ou couverts
            par une validation. L&apos;appariement <strong>explique</strong>, il ne solde jamais :
            seule la validation solde un écart.
          </p>

          {lignes.length === 0 && (
            <p className="alerte alerte--info" role="status">
              Aucun mouvement sur ce filtre depuis l&apos;ancrage du 01/01/2026.
            </p>
          )}

          {lignes.length > 0 && (
            <>
              {visibles.length < lignes.length && (
                <p className="alerte alerte--info" role="status">
                  {lignes.length.toLocaleString('fr-FR')} lignes au total, {LIGNES_AFFICHEES}{' '}
                  affichées. L&apos;export Excel contient l&apos;intégralité du détail.{' '}
                  <button
                    type="button"
                    className="bouton bouton--lien"
                    onClick={() => setToutAfficher(true)}
                  >
                    Tout afficher à l&apos;écran
                  </button>
                </p>
              )}

              <div className="tableau-defilant">
                <table className="tableau">
                  <thead>
                    <tr>
                      <th scope="col">
                        <span className="lecteur-seul">Déplier</span>
                      </th>
                      <th scope="col">Jour</th>
                      <th scope="col">Code</th>
                      <th scope="col">GERECD</th>
                      <th scope="col">GESUPD</th>
                      <th scope="col">Journal</th>
                      <th scope="col">Net WMS</th>
                      <th scope="col">Entrées douane</th>
                      <th scope="col">Sorties douane</th>
                      <th scope="col">Net douane</th>
                      <th scope="col">Écart du jour</th>
                      <th scope="col">Cumul du code</th>
                      <th scope="col">Cumul de la clé</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map((ligne) => (
                      <Fragment key={`${ligne.jour}-${ligne.codpro}`}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className="bouton-deplier"
                            aria-expanded={depliees.includes(ligne.jour)}
                            aria-label={`${depliees.includes(ligne.jour) ? 'Replier' : 'Déplier'} les mouvements du ${formaterDate(ligne.jour)}`}
                            onClick={() =>
                              setDepliees((jours) =>
                                jours.includes(ligne.jour)
                                  ? jours.filter((j) => j !== ligne.jour)
                                  : [...jours, ligne.jour],
                              )
                            }
                          >
                            {depliees.includes(ligne.jour) ? '−' : '+'}
                          </button>
                        </td>
                        <td>
                          <span
                            className={`pastille pastille--${ligne.etat.toLowerCase()}`}
                            title={
                              ligne.etat === 'VERT'
                                ? 'Tout est apparié ou validé ce jour-là.'
                                : ligne.etat === 'ORANGE'
                                  ? 'Journée expliquée, dont une partie par un rapprochement manuel.'
                                  : 'Il reste des mouvements sans contrepartie ni validation.'}
                          >
                            <span className="lecteur-seul">
                              {ligne.etat === 'VERT'
                                ? 'Journée verte'
                                : ligne.etat === 'ORANGE'
                                  ? 'Journée orange'
                                  : 'Journée rouge'}
                            </span>
                          </span>{' '}
                          {formaterDate(ligne.jour)}
                        </td>
                        <td title={ligne.designation ?? undefined}>{ligne.codpro}</td>
                        <td className="droite">{formaterNombre(ligne.gerecd)}</td>
                        <td className="droite">{formaterNombre(ligne.gesupd)}</td>
                        <td className="droite">{formaterNombre(ligne.jnl)}</td>
                        <td className="droite">{formaterNombre(ligne.wmsNet)}</td>
                        <td className="droite">{formaterNombre(ligne.douaneEntrees)}</td>
                        <td className="droite">{formaterNombre(ligne.douaneSorties)}</td>
                        <td className="droite">{formaterNombre(ligne.douaneNet)}</td>
                        <td className={`droite ${classeMontant(ligne.ecartJour)}`}>
                          {formaterNombre(ligne.ecartJour)}
                        </td>
                        <td className={`droite ${classeMontant(ligne.ecartCumuleCode)}`}>
                          {formaterNombre(ligne.ecartCumuleCode)}
                        </td>
                        <td className={`droite ${classeMontant(ligne.ecartCumuleCle)}`}>
                          {formaterNombre(ligne.ecartCumuleCle)}
                        </td>
                      </tr>
                      {depliees.includes(ligne.jour) && (
                        <tr className="ligne-detail">
                          <td colSpan={13}>
                            <MouvementsDuJour id={id} jour={ligne.jour} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
